import { create } from "zustand";
import { CompanyCardDataDto } from "@/components/DetailsSection/components/DetailsContent/types";
import { CompanyCardStatus } from "@/Pages/Dashboard/LeftSide/Connectivity/types";
import { ResourceDownloadsStatus } from "@/Pages/Dashboard/LeftSide/ResourceDownloads/types";
import { CREDITS_HEALTH, CreditsDto } from "@/Pages/Dashboard/LeftSide/types";
import {
  DeviceStatus,
  StatusWidgetDto
} from "@/Pages/Dashboard/shared/DeviceStatus/types";
import { NewsDto } from "@/Pages/Dashboard/shared/News";
import { CCExpirySummaryDto } from "@/Pages/Dashboard/types";

interface DashboardStore {
  // Loading states
  isDashboardLoading: boolean;
  isInitialLoading: boolean;

  // Data states
  newsHeight: number;
  newsData: NewsDto[] | null;
  unpublishedReleaseNote: NewsDto[];
  deviceStatus: StatusWidgetDto | null;
  driverDownloadsStatus: StatusWidgetDto | null;
  vehicleDownloadsStatus: StatusWidgetDto | null;
  companyCards: CompanyCardDataDto[] | null;
  ccExpiry: CCExpirySummaryDto | null;
  ccStatus: StatusWidgetDto | null;
  credits: CreditsDto | null;

  newsScrollOffset: number;

  // Error states
  newsError: boolean;
  deviceStatusError: boolean;
  companyCardsError: boolean;
  ccExpiryError: boolean;
  ccStatusError: boolean;
  creditsError: boolean;

  driverDownloadsError: boolean;
  vehicleDownloadsError: boolean;

  // Setters for loading states
  setIsDashboardLoading: (isLoading: boolean) => void;
  setIsInitialLoading: (isLoading: boolean) => void;

  // Setters for data states
  setNewsHeight: (newsHeight: number) => void;
  setNewsData: (data: NewsDto[] | null) => void;
  setUnpublishedReleaseNote: (data: NewsDto[]) => void;
  setDeviceStatus: (data: StatusWidgetDto | null) => void;
  setCompanyCards: (data: CompanyCardDataDto[] | null) => void;
  setCCExpiry: (data: CCExpirySummaryDto | null) => void;
  setCCStatus: (data: StatusWidgetDto | null) => void;
  setCredits: (data: CreditsDto | null) => void;

  setDriverDownloadsStatus: (data: StatusWidgetDto | null) => void;
  setVehicleDownloadsStatus: (data: StatusWidgetDto | null) => void;

  setNewsScrollOffset: (offset: number) => void;

  // Setters for error states
  setNewsError: (hasError: boolean) => void;
  setDeviceStatusError: (hasError: boolean) => void;
  setCompanyCardsError: (hasError: boolean) => void;
  setCCExpiryError: (hasError: boolean) => void;
  setCCStatusError: (hasError: boolean) => void;
  setCreditsError: (hasError: boolean) => void;

  setDriverDownloadsError: (hasError: boolean) => void;
  setVehicleDownloadsError: (hasError: boolean) => void;

  // Reset function
  resetDashboardData: () => void;
}

const dashboardDeviceStatusInitialSummary = [
  {
    name: DeviceStatus?.Online,
    count: 0,
    companies: []
  },
  {
    name: DeviceStatus?.Offline,
    count: 0,
    companies: []
  },
  {
    name: DeviceStatus?.Inactive,
    count: 0,
    companies: []
  },
  {
    name: DeviceStatus?.Deactivated,
    count: 0,
    companies: []
  }
];

const driverDownloadStatusInitialSummary = [
  {
    name: ResourceDownloadsStatus?.OnTime,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.DueSoon,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.Overdue,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.NoDownloads,
    count: 0,
    companies: []
  }
];

const vehicleDownloadStatusInitialSummary = [
  {
    name: ResourceDownloadsStatus?.OnTime,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.DueSoon,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.Overdue,
    count: 0,
    companies: []
  },
  {
    name: ResourceDownloadsStatus?.NoDownloads,
    count: 0,
    companies: []
  }
];

const dashboardCCStatusInitialSummary = [
  {
    name: CompanyCardStatus?.Online,
    count: 0,
    companies: []
  },
  {
    name: CompanyCardStatus?.Offline,
    count: 0,
    companies: []
  },
  {
    name: CompanyCardStatus?.Inactive,
    count: 0,
    companies: []
  },
  {
    name: CompanyCardStatus?.Deactivated,
    count: 0,
    companies: []
  }
];

export const useDashboardStore = create<DashboardStore>()(set => ({
  // Initial loading states
  isDashboardLoading: false,
  isInitialLoading: true,

  // Initial data states
  newsHeight: 1,
  newsData: [],
  unpublishedReleaseNote: [],
  deviceStatus: {
    totalCount: 0,
    summary: dashboardDeviceStatusInitialSummary
  },

  driverDownloadsStatus: {
    totalCount: 0,
    summary: driverDownloadStatusInitialSummary
  },

  vehicleDownloadsStatus: {
    totalCount: 0,
    summary: vehicleDownloadStatusInitialSummary
  },

  companyCards: [],
  ccExpiry: null,
  ccStatus: {
    totalCount: 0,
    summary: dashboardCCStatusInitialSummary
  },
  credits: {
    total: 0,
    reserved: 0,
    available: 0,
    balance: 0,
    totalWithOverdraft: 0,
    health: CREDITS_HEALTH.Healthy,
    history: [],
    monthlyBalance: [],
    expiresAt: "",
    overDraftEnabled: false,
    overDraftAmount: 0,
    isDemo: false,
    isDemoUsedUp: false,
    isDemoExpired: false
  },

  newsScrollOffset: 0,

  // Initial error states
  newsError: false,
  deviceStatusError: false,
  companyCardsError: false,
  ccExpiryError: false,
  ccStatusError: false,
  creditsError: false,
  driverDownloadsError: false,
  vehicleDownloadsError: false,

  // Loading state setters
  setIsDashboardLoading: isLoading => set({ isDashboardLoading: isLoading }),
  setIsInitialLoading: isLoading => set({ isInitialLoading: isLoading }),

  // Data state setters
  setNewsHeight: newsHeight => set({ newsHeight: newsHeight }),
  setNewsData: data => set({ newsData: data }),
  setUnpublishedReleaseNote: data => set({ unpublishedReleaseNote: data }),
  setDeviceStatus: data => set({ deviceStatus: data }),
  setDriverDownloadsStatus: data => set({ driverDownloadsStatus: data }),
  setVehicleDownloadsStatus: data => set({ vehicleDownloadsStatus: data }),
  setCompanyCards: data => set({ companyCards: data }),
  setCCExpiry: data => set({ ccExpiry: data }),
  setCCStatus: data => set({ ccStatus: data }),
  setCredits: data => {
    if (data) {
      const updatedData = {
        ...data,
        monthlyBalance: data.monthlyBalance.map(item => ({
          ...item,
          date: item.date.split("T")[0]
        }))
      };

      set({ credits: updatedData });
    } else {
      set({ credits: data });
    }
  },

  setNewsScrollOffset: offset => set({ newsScrollOffset: offset }),

  // Error state setters
  setNewsError: hasError => set({ newsError: hasError }),
  setDeviceStatusError: hasError => set({ deviceStatusError: hasError }),
  setCompanyCardsError: hasError => set({ companyCardsError: hasError }),
  setCCExpiryError: hasError => set({ ccExpiryError: hasError }),
  setCCStatusError: hasError => set({ ccStatusError: hasError }),
  setCreditsError: hasError => set({ creditsError: hasError }),
  setDriverDownloadsError: hasError => set({ driverDownloadsError: hasError }),
  setVehicleDownloadsError: hasError =>
    set({ vehicleDownloadsError: hasError }),

  // Reset function for page changes
  resetDashboardData: () =>
    set({
      newsData: null,
      deviceStatus: null,
      companyCards: null,
      ccStatus: null
    })
}));
